'use client';

import { useCallback, useEffect } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useAppEvents } from '@kit/shared/events';
import { isRecordingActive } from '@kit/shared/recording-state';
import { getCookieDomain } from '@kit/supabase/get-cookie-domain';
import { useAuthChangeListener } from '@kit/supabase/hooks/use-auth-change-listener';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import {
  getSubdomainBaseDomain,
  getTeamSubdomain,
} from '@kit/supabase/subdomain';

import { TEAM_SUBDOMAIN_PRIVATE_PREFIXES } from './auth-private-prefixes';

export function AuthProvider(props: React.PropsWithChildren) {
  const dispatchEvent = useDispatchAppEventFromAuthEvent();

  useImplicitFlowHandler();
  useStaleAuthCookieCleanup();
  useSessionResyncOnVisibility();

  const onEvent = useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      dispatchEvent(event, session?.user.id, {
        email: session?.user.email ?? '',
      });
    },
    [dispatchEvent],
  );

  useAuthChangeListener({
    // isPrivateFn overrides the hook's default prefix check.
    isPrivateFn: isTeamSubdomainPrivatePath,
    onEvent,
    isOperationInProgress: () => isRecordingActive(),
  });

  return props.children;
}

/**
 * On a team subdomain (e.g. sounders.arkero.ai), middleware rewrites every
 * top-level path to /home/{slug}{path}, so any non-auth path is an authenticated
 * app surface. Rather than hand-maintaining a route allowlist that drifts as new
 * team features ship, we detect the hostname and treat the whole surface as
 * private. TEAM_SUBDOMAIN_PRIVATE_PREFIXES remains the fallback for non-team
 * hostnames (auth subdomain, base domain, localhost).
 */
function isTeamSubdomainPrivatePath(pathName: string): boolean {
  if (pathName.startsWith('/auth/')) return false;

  if (
    typeof window !== 'undefined' &&
    getTeamSubdomain(window.location.hostname) !== null
  ) {
    return true;
  }

  return TEAM_SUBDOMAIN_PRIVATE_PREFIXES.some((prefix) =>
    pathName.startsWith(prefix),
  );
}

/**
 * Clear stale auth cookies on /auth/* pages to prevent the Supabase SDK's
 * autoRefreshToken from entering a retry loop on a consumed refresh token.
 *
 * If the user reaches an auth page with auth cookies, those cookies are stale
 * (middleware redirects authenticated users away from /auth/*). We clear them
 * via document.cookie (handles both host-specific and parent-domain cookies)
 * and call signOut({ scope: 'local' }) to reset SDK internal state.
 */
function useStaleAuthCookieCleanup() {
  const client = useSupabase();

  useEffect(() => {
    if (!window.location.pathname.startsWith('/auth/')) {
      return;
    }

    // Don't clear cookies on the MFA verification page — the user needs
    // their AAL1 session to list and verify MFA factors.
    if (window.location.pathname.startsWith('/auth/verify')) {
      return;
    }

    const authCookies = document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((c) => c.includes('auth-token'));

    if (authCookies.length === 0) {
      return;
    }

    // Delete each cookie on both the exact hostname (no domain attr)
    // and the parent domain, since we don't know which was originally set
    const cookieDomain = getCookieDomain(window.location.hostname);

    for (const raw of authCookies) {
      const name = raw.split('=')[0]!;

      document.cookie = `${name}=; Max-Age=0; Path=/`;

      if (cookieDomain) {
        document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${cookieDomain}`;
      }
    }

    // Reset SDK internal state so it stops any in-flight refresh attempts
    void client.auth.signOut({ scope: 'local' });
  }, [client]);
}

/**
 * Re-sync auth when returning to a tab after inactivity (focus/pageshow/visibility).
 * This handles stale sessions where middleware sees old claims until the browser
 * performs a refresh-capable auth call.
 */
function useSessionResyncOnVisibility() {
  const client = useSupabase();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let inFlight = false;
    let lastSyncAt = 0;

    const minSyncIntervalMs = 15_000;

    const syncSession = async () => {
      // eslint-disable-next-line no-restricted-syntax -- Date.now() is restricted in render but allowed in effect callbacks.
      const now = Date.now();

      if (inFlight || now - lastSyncAt < minSyncIntervalMs) {
        return;
      }

      // Don't redirect mid-recording — the session resync triggers a
      // window.location.assign() which would abort an active recording.
      if (isRecordingActive()) {
        return;
      }

      const pathName = window.location.pathname;

      // Unified with the SIGNED_OUT decision — otherwise the resync silently
      // skips team-subdomain paths like /vault, /planning that middleware
      // rewrites to /home/{slug}{path}.
      if (!isTeamSubdomainPrivatePath(pathName)) {
        return;
      }

      inFlight = true;
      lastSyncAt = now;

      try {
        const { data, error } = await client.auth.getSession();

        if (data.session) {
          return;
        }

        // Network/transient errors should not force sign-out. Let middleware
        // handle the next request and retry on the next visibility/focus event.
        if (error && !isTerminalAuthError(error)) {
          return;
        }

        const next = `${window.location.pathname}${window.location.search}`;
        const redirectPath = `/auth/sign-in?next=${encodeURIComponent(next)}`;

        await client.auth.signOut({ scope: 'local' }).catch(() => undefined);
        window.location.assign(redirectPath);
      } finally {
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncSession();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      // Only sync on bfcache restores, not fresh page loads (handled by mount)
      if (event.persisted) {
        void syncSession();
      }
    };

    void syncSession();

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [client]);
}

function isTerminalAuthError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as { status?: unknown; message?: unknown };
  const status = typeof maybeError.status === 'number' ? maybeError.status : -1;

  if ([400, 401, 403].includes(status)) {
    return true;
  }

  const message =
    typeof maybeError.message === 'string'
      ? maybeError.message.toLowerCase()
      : '';

  return (
    message.includes('refresh token') ||
    message.includes('invalid grant') ||
    message.includes('jwt')
  );
}

/**
 * Handles implicit OAuth flow tokens in the URL hash.
 *
 * When IdP-initiated SSO (e.g., Okta portal) redirects to the app,
 * Supabase returns tokens in the URL fragment (#access_token=...).
 * The @supabase/ssr library hardcodes flowType: 'pkce', causing the
 * built-in token detection to reject implicit flow tokens with
 * AuthPKCEGrantCodeExchangeError. This hook manually extracts
 * the tokens and establishes the session.
 */
function useImplicitFlowHandler() {
  const client = useSupabase();

  useEffect(() => {
    const hash = window.location.hash;

    if (!hash || !hash.includes('access_token=')) {
      return;
    }

    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (!accessToken || !refreshToken) {
      return;
    }

    // Clear hash immediately to prevent re-processing
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    );

    void client.auth
      .setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      })
      .then(async ({ error }) => {
        if (error) {
          console.error(
            '[implicit-flow] Failed to set session:',
            error.message,
          );
          return;
        }

        // Look up the user's team account to redirect directly to their subdomain
        const { data: teams } = await client
          .from('accounts')
          .select('slug')
          .eq('is_personal_account', false)
          .not('slug', 'is', null)
          .limit(1);

        const teamSlug = teams?.[0]?.slug;

        if (teamSlug) {
          // Redirect to team subdomain (e.g., sounders.arkero.ai)
          const baseDomain = getSubdomainBaseDomain(window.location.hostname);
          window.location.href = `${window.location.protocol}//${teamSlug}.${baseDomain}`;
        } else {
          // No team found — fall back to /home for team selection
          window.location.href = '/home';
        }
      });
  }, [client]);
}

function useDispatchAppEventFromAuthEvent() {
  const { emit } = useAppEvents();
  const monitoring = useMonitoring();

  return useCallback(
    (
      type: AuthChangeEvent,
      userId: string | undefined,
      traits: Record<string, string> = {},
    ) => {
      switch (type) {
        case 'INITIAL_SESSION':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'SIGNED_IN':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'USER_UPDATED':
          emit({
            type: 'user.updated',
            payload: { userId: userId!, ...traits },
          });

          break;

        case 'SIGNED_OUT':
          // Single chokepoint for every useSignOut() call site. Lets analytics
          // clear per-tenant super properties (e.g. account_slug) so they don't
          // leak into the next user's session on a shared browser.
          emit({
            type: 'user.signedOut',
            payload: {},
          });

          break;
      }
    },
    [emit, monitoring],
  );
}
